import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "../utils/cn";
import { useToast } from "../contexts/ToastContext";

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function WaitlistModal({ isOpen, onClose }: WaitlistModalProps) {
  const [email, setEmail] = useState("");
  const [automationGoal, setAutomationGoal] = useState("");
  const [telegramId, setTelegramId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!email.trim()) {
      showToast({
        type: "error",
        title: "Email Required",
        message: "Please enter your email address",
      });
      return;
    }

    if (!automationGoal.trim()) {
      showToast({
        type: "error",
        title: "Automation Goal Required",
        message: "Please tell us what you'd like to automate",
      });
      return;
    }

    if (!telegramId.trim()) {
      showToast({
        type: "error",
        title: "Telegram ID Required",
        message: "Please enter your Telegram ID",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          automation_goal: automationGoal.trim(),
          telegram_id: telegramId.trim(),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showToast({
          type: "success",
          title: "Successfully Joined Waitlist!",
          message:
            "Thank you for joining! You qualify for a special de5 NFT (coming soon).",
        });
        setEmail("");
        setAutomationGoal("");
        setTelegramId("");
        onClose();
      } else {
        showToast({
          type: "error",
          title: "Registration Failed",
          message: data.error || "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      showToast({
        type: "error",
        title: "Network Error",
        message:
          "Unable to connect. Please check your connection and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 z-50 backdrop-blur-sm"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Header */}
              <div
                className="relative p-6 text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #e95c49 0%, #d94a39 100%)",
                }}
              >
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
                <h2 className="text-2xl font-oxanium font-bold mb-2">
                  Join Waitlist
                </h2>
                <p className="text-white/90 text-sm">
                  Hi, welcome to de5.ai. Please share your details with us so we
                  can provide valuable timely updates about our product.
                </p>
                <p className="text-white/90 text-sm mt-2">
                  And, you qualify for a special de5 NFT (coming soon), which
                  will have multiple benefits within our ecosystem further down
                  the roadmap.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    placeholder="your.email@example.com"
                    className={cn(
                      "w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600",
                      "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                      "focus:ring-2 focus:ring-primary-400 focus:border-transparent",
                      "placeholder-gray-500 dark:placeholder-gray-400",
                      "transition-all duration-200",
                      isSubmitting && "opacity-50 cursor-not-allowed"
                    )}
                  />
                </div>

                {/* Automation Goal */}
                <div>
                  <label
                    htmlFor="automationGoal"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    What would you want to automate using de5.ai?{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="automationGoal"
                    value={automationGoal}
                    onChange={(e) => setAutomationGoal(e.target.value)}
                    disabled={isSubmitting}
                    placeholder="Tell us about your DeFi automation goals..."
                    rows={4}
                    className={cn(
                      "w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600",
                      "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                      "focus:ring-2 focus:ring-primary-400 focus:border-transparent",
                      "placeholder-gray-500 dark:placeholder-gray-400",
                      "transition-all duration-200",
                      "resize-none",
                      isSubmitting && "opacity-50 cursor-not-allowed"
                    )}
                  />
                </div>

                {/* Telegram ID */}
                <div>
                  <label
                    htmlFor="telegramId"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    What is your Telegram id?{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="telegramId"
                    type="text"
                    value={telegramId}
                    onChange={(e) => setTelegramId(e.target.value)}
                    disabled={isSubmitting}
                    placeholder="@yourusername"
                    className={cn(
                      "w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600",
                      "bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                      "focus:ring-2 focus:ring-primary-400 focus:border-transparent",
                      "placeholder-gray-500 dark:placeholder-gray-400",
                      "transition-all duration-200",
                      isSubmitting && "opacity-50 cursor-not-allowed"
                    )}
                  />
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className={cn(
                    "w-full px-6 py-4 rounded-lg font-semibold text-lg text-white",
                    "transition-all duration-300",
                    "shadow-lg",
                    isSubmitting && "opacity-50 cursor-not-allowed"
                  )}
                  style={{
                    backgroundColor: "#e95c49",
                    boxShadow: "0 10px 25px rgba(233, 92, 73, 0.25)",
                  }}
                  whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                  whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Submitting...
                    </span>
                  ) : (
                    "Submit"
                  )}
                </motion.button>

                {/* Privacy Note */}
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Your information will be used to provide updates about de5.ai
                  and your special NFT benefits. We respect your privacy.
                </p>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
